<script setup lang="ts">
import type { EditRequestStatus } from '@forgd/app-layer-core/layers/auth/utils/project-management'
import { useAppToast } from '@forgd/app-layer-core/composables/useAppToast'
import { useAuth } from '@forgd/app-layer-core/layers/auth/composables/useAuth'
import { useProjectEditors } from '@forgd/app-layer-core/layers/auth/composables/useProjectEditors'
import { Alert, AlertDescription } from '@forgd/ui-kit/alert'
import { Avatar, AvatarGroup, AvatarInitials } from '@forgd/ui-kit/avatar'
import { Badge } from '@forgd/ui-kit/badge'
import { Button } from '@forgd/ui-kit/button'
import { Popover, PopoverContent, PopoverTrigger } from '@forgd/ui-kit/popover'
import { Separator } from '@forgd/ui-kit/separator'
import { Tooltip, TooltipContent, TooltipTrigger } from '@forgd/ui-kit/tooltip'
import {
  ArrowsRightLeftIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  InformationCircleIcon,
  PencilIcon,
  XMarkIcon,
} from '@heroicons/vue/24/outline'
import { formatDuration, intervalToDuration, intlFormatDistance } from 'date-fns'
import { storeToRefs } from 'pinia'
import { computed, onUnmounted, ref, watch } from 'vue'
import { useProjectPresence } from '../../composables/useProjectPresence'

const auth = useAuth()
const projectEditors = useProjectEditors()
const toast = useAppToast()
const presence = useProjectPresence()
const { presentUsers } = storeToRefs(presence)

const projectTickerOrName = computed(() => auth.project?.ticker || auth.project?.name)

function viewerName(user: { firstName: string | null, lastName: string | null, email?: string | null }) {
  if (user.firstName && user.lastName) {
    return `${user.firstName} ${user.lastName}`
  }
  return user.email ?? ''
}
const awaitingDecision = computed(() => projectEditors.pendingEditor?.id === auth.me?.id)

const isPopoverOpen = ref(false)
const isRequestDenied = ref(false)
const isPendingModalOpen = ref(false)
const expanded = useNavExpanded()

type EditAction = 'approve' | 'reject' | 'request'
const actionInProgress = ref<EditAction | null>(null)

const autoApproveIntervalLabel = computed(() => {
  const seconds = projectEditors.autoApproveIntervalSeconds || 0
  const duration = intervalToDuration({ start: 0, end: seconds * 1000 })
  return formatDuration(duration, { format: ['hours', 'minutes', 'seconds'] })
})

const countdownInterval = ref<ReturnType<typeof setInterval> | null>(null)
const autoApproveCountdown = ref<string | null>(null)
const autoApproveLabel = computed(() => autoApproveCountdown.value ?? autoApproveIntervalLabel.value)

function updateCountdown() {
  if (!projectEditors.autoApproveAt) {
    autoApproveCountdown.value = null
    return
  }

  const timeLeft = projectEditors.autoApproveAt.getTime() - Date.now()

  if (timeLeft <= 0) {
    autoApproveCountdown.value = null
    return
  }

  if (timeLeft < 3_000) {
    autoApproveCountdown.value = 'in a few seconds'
    return
  }

  autoApproveCountdown.value = intlFormatDistance(projectEditors.autoApproveAt, Date.now())
}

function clearCountdown() {
  if (!countdownInterval.value) {
    return
  }
  clearInterval(countdownInterval.value)
  countdownInterval.value = null
  autoApproveCountdown.value = null
}

function getActionConfig(): Record<EditAction, {
  mutate: (req: { path: { projectId: string } }) => Promise<unknown>
  toast: string | { title: string, description: string }
}> {
  return {
    request: {
      mutate: projectEditors.request,
      toast: {
        title: 'Editor has been notified',
        description: 'Your request for editing control was sent successfully.',
      },
    },
    approve: {
      mutate: projectEditors.approve,
      toast: `You have given editing control. You can keep navigating ${projectTickerOrName.value} as a viewer.`,
    },
    reject: {
      mutate: projectEditors.reject,
      toast: 'You rejected the editing control request.',
    },
  }
}

async function processRequest(action: EditAction) {
  if (actionInProgress.value) {
    return false
  }

  const actionConfig = getActionConfig()

  try {
    actionInProgress.value = action
    await actionConfig[action].mutate({
      path: { projectId: String(auth.project?.id) },
    })
    toast.success(actionConfig[action].toast)
    return true
  }
  catch {
    if (projectEditors.error?.status === 409) {
      toast.error(projectEditors.error.message)
      return
    }
    toast.error('Something went wrong. Please try again.')
    return false
  }
  finally {
    actionInProgress.value = null
  }
}

async function requestToEdit() {
  const success = await processRequest('request')
  if (!success) {
    return
  }
  // Refetch to update the UI; we need to get the autoApproveAt from the API
  await projectEditors.refetch()
  isRequestDenied.value = false
}

async function onApproveClick() {
  const success = await processRequest('approve')
  if (!success) {
    return
  }
  isPendingModalOpen.value = false
}

async function onRejectClick() {
  const success = await processRequest('reject')
  if (!success) {
    return
  }
  clearCountdown()
}

function handleEditRequestStatusChange(val: EditRequestStatus) {
  isPendingModalOpen.value = false
  isRequestDenied.value = false

  switch (val) {
    case 'pending_request':
      isPendingModalOpen.value = true
      break
    case 'approved':
      toast.success({
        title: 'Editing Controls Granted',
        description: 'You can now make edits to the project.',
      })
      clearCountdown()
      break
    case 'denied':
      isRequestDenied.value = true
      toast.error({
        title: 'Editing Controls Denied',
        description: `${projectEditors.activeEditor?.name} is still editing the project. Please try again later.`,
      })
      clearCountdown()
      break
  }
}

watch(
  () => projectEditors.editRequestStatus,
  val => handleEditRequestStatusChange(val),
  { immediate: true },
)

// Start/stop countdown timer
watch(() => projectEditors.autoApproveAt, (autoApproveAt) => {
  clearCountdown()

  if (autoApproveAt) {
    updateCountdown()
    countdownInterval.value = setInterval(updateCountdown, 1000)
  }
  else {
    autoApproveCountdown.value = null
  }
}, { immediate: true })

onUnmounted(() => clearCountdown())
</script>

<template>
  <div v-if="isPendingModalOpen" data-testid="pending-request-modal" class="fixed top-3 z-30 w-80 rounded-lg border-neutral-600 bg-neutral-400 p-4 shadow-md" :class="expanded ? 'left-68' : 'left-16'">
    <div class="mb-2 flex items-center justify-between">
      <div class="flex items-center gap-2 text-sm font-semibold">
        <ArrowsRightLeftIcon class="size-3 shrink-0 text-primal" aria-hidden="true" />
        Transfer Edit Control Request
      </div>
      <button type="button" aria-label="Close" class="inline-flex text-primal" @click="isPendingModalOpen = false">
        <XMarkIcon class="size-4" aria-hidden="true" />
      </button>
    </div>
    <div class="mb-4 space-y-8 text-sm text-secondary">
      <div>
        <span class="font-semibold">{{ projectEditors.pendingEditor?.name }}</span> is requesting to make edits in project {{ projectTickerOrName }}.
      </div>
      <div>
        If ignored, editing control will be granted {{ autoApproveLabel }}.
      </div>
    </div>
    <div class="flex items-center gap-2">
      <Button data-testid="reject-request" size="sm" variant="outline" class="w-35" :loading="actionInProgress === 'reject'" @click="onRejectClick">
        Reject
      </Button>
      <Button data-testid="grant-control" size="sm" class="w-35" :loading="actionInProgress === 'approve'" @click="onApproveClick">
        Grant Control
      </Button>
    </div>
  </div>
  <Popover v-model:open="isPopoverOpen">
    <PopoverTrigger as-child>
      <Button data-testid="project-edit-control" variant="outline" size="sm" class="py-2">
        <component :is="projectEditors.isActiveEditor ? PencilIcon : EyeIcon" class="size-3! text-primal" aria-hidden="true" />
        <span class="text-xs font-semibold">{{ projectEditors.isActiveEditor ? 'Editor' : 'Viewer' }}</span>
        <ChevronDownIcon class="text-primal transition" :class="isPopoverOpen ? 'rotate-180' : ''" aria-hidden="true" />
      </Button>
    </PopoverTrigger>
    <PopoverContent
      side="top"
      align="end"
      class="w-85 overflow-visible p-0"
    >
      <template v-if="projectEditors.isActiveEditor">
        <div class="space-y-2 p-4">
          <div class="flex items-center gap-1">
            <PencilIcon class="size-3 shrink-0 text-primal" aria-hidden="true" />
            <span class="text-xs font-semibold">You are the current editor of {{ projectTickerOrName }}</span>
          </div>
          <span class="text-xs text-secondary">As an editor, you have permission to change data and settings of this project.</span>
        </div>
      </template>
      <template v-else>
        <div class="p-4">
          <Badge variant="gray" size="md" class="w-full justify-center">
            You are navigating as viewer
            <EyeIcon aria-hidden="true" />
          </Badge>
        </div>
        <Separator />
        <div class="space-y-4 p-4">
          <div v-if="projectEditors.activeEditor" class="text-xs text-secondary">
            <span class="font-semibold text-primal">{{ projectEditors.activeEditor.name }}</span> is the current editor. If you want to make edits to this project, request editing control.
          </div>
          <div v-if="awaitingDecision">
            <Badge variant="green">
              Controls Requested
              <CheckCircleIcon aria-hidden="true" />
            </Badge>
          </div>
          <Button v-else data-testid="request-editing-control" variant="outline" size="sm" :loading="actionInProgress === 'request'" @click="requestToEdit()">
            Request Editing Control
          </Button>
          <Alert v-if="isRequestDenied" variant="warning" class="text-xs">
            <ExclamationTriangleIcon aria-hidden="true" />
            <AlertDescription>Control request has been denied by the editor. Please try again later.</AlertDescription>
          </Alert>
          <Alert v-else class="text-xs">
            <InformationCircleIcon aria-hidden="true" />
            <AlertDescription>Control might be granted {{ autoApproveLabel }} if the current editor does not take action.</AlertDescription>
          </Alert>
        </div>
      </template>
      <Separator />
      <div data-testid="current-project-viewers" class="flex items-center justify-between p-4">
        <div class="text-xs text-secondary">
          Current project viewers
        </div>
        <AvatarGroup>
          <Tooltip v-for="user in presentUsers" :key="user.id">
            <TooltipTrigger as-child>
              <Avatar size="sm" bordered tabindex="0" :data-testid="`ui-avatar-initials-${user.id}`">
                <AvatarInitials :first-name="user.firstName" :last-name="user.lastName" />
              </Avatar>
            </TooltipTrigger>
            <TooltipContent>{{ viewerName(user) }}</TooltipContent>
          </Tooltip>
        </AvatarGroup>
      </div>
    </PopoverContent>
  </Popover>
</template>
